import { useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import type { ChartData } from 'chart.js'
import startCase from 'lodash/startCase'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'

import TrashIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { CalendarFormInput } from '@/modules/online-mass-messages/ui/time-calendar/calendar-input'

import { formatChartEntryDate, parseChartEntryDate } from './utils'

interface Props {
  isOpen: boolean
  onClose: () => void
  entity: string
  data?: ChartData<'line'>
  onEdit: (entries: ChartData<'line'>) => void
  chartLineColor: string
}

const schema = z.object({
  entries: z.array(
    z.object({
      date: z.date(),
      value: z.number(),
    }),
  ),
})

type FormValues = z.infer<typeof schema>
const FORM_ID = 'edit-line-chart-form'

export const EditLineChartDialog = ({
  isOpen,
  onClose,
  entity,
  data,
  onEdit,
  chartLineColor,
}: Props) => {
  const entries = useMemo(() => {
    if (!data?.labels) {
      return []
    }

    const entries = data.labels.reduce<FormValues['entries']>(
      (acc, label, index) => {
        const value = data.datasets[0].data[index] as number

        if (value !== 0) {
          acc.push({
            date: parseChartEntryDate(label as string),
            value: value,
          })
        }

        return acc
      },
      [],
    )

    if (entries.length === 0) {
      entries.push({ date: new Date(), value: 0 })
    }

    return entries
  }, [data])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      entries,
    },
  })
  const { control } = form
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  })

  useEffect(() => {
    form.reset({ entries })
  }, [entries, form])

  const handleSubmit = form.handleSubmit((values) => {
    const chartData = values.entries.reduce<
      Required<Pick<ChartData<'line'>, 'datasets' | 'labels'>>
    >(
      (acc, { date, value }) => {
        acc.labels.push(formatChartEntryDate(date))
        acc.datasets[0].data.push(value)

        return acc
      },
      {
        labels: [],
        datasets: [{ data: [], label: entity, borderColor: chartLineColor }],
      },
    )

    onEdit(chartData)
    onClose()
  })

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[560px]"
      childrenClassName="space-y-6"
      afterLeave={form.reset}
    >
      <Dialog.Header>
        <Dialog.Title>{startCase(entity)}</Dialog.Title>
      </Dialog.Header>

      <FormProvider {...form}>
        <form
          className="max-h-[410px] space-y-6 overflow-auto"
          id={FORM_ID}
          onSubmit={handleSubmit}
        >
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex items-end gap-4"
            >
              <CalendarFormInput
                name={`entries.${index}.date`}
                label="Date"
                className="bg-white dark:bg-transparent"
                format="LLL dd, y - hh:mmaaa"
              />
              <FormField
                name={`entries.${index}.value`}
                label="Value"
                defaultValue={field.value}
                type="number"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="mb-1"
                disabled={fields.length === 1}
                onClick={() => remove(index)}
              >
                <TrashIcon className="text-danger-default h-6 w-6" />
              </Button>
            </div>
          ))}
        </form>
      </FormProvider>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => append({ date: new Date(), value: 0 })}
      >
        Add new value
      </Button>

      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          type="submit"
          form={FORM_ID}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
